import { FC, forwardRef, InputHTMLAttributes, ReactNode } from 'react'

import styled, { css } from 'styled-components'

import { variables } from 'core/styles'

export type InputVariant = 'outline' | 'underline' | 'none'

export type TextInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'prefix'> & {
  className?: string
  /** @deprecated use onChange instead */
  onValueChange?: (val: string) => void
  variant?: InputVariant
  width?: string
  prefix?: ReactNode
  postfix?: ReactNode
}

export const NumberTextInput: FC<Omit<TextInputProps, 'onValueChange'>> = ({
  placeholder = '0',
  onChange,
  onBlur,
  ...props
}) => (
  <TextInput
    onChange={(e) => {
      if (Number.isNaN(Number(e.target.value))) {
        e.preventDefault()
        return
      }

      onChange?.(e)
    }}
    onBlur={(e) => {
      if (e.target.value !== '') {
        e.target.value = String(Number(e.target.value))
        onChange?.(e)
      }

      onBlur?.(e)
    }}
    placeholder={placeholder}
    {...props}
  />
)

const amountPattern = /^(\d+)?([.]?\d{0,2})?$/

export const AmountTextInput = forwardRef<HTMLInputElement, Omit<TextInputProps, 'onValueChange'>>(
  ({ prefix = '$', placeholder = '0.00', onChange, onBlur, ...props }, ref) => (
    <TextInput
      onChange={(e) => {
        if (!amountPattern.test(e.target.value)) {
          e.preventDefault()
          return
        }

        onChange?.(e)
      }}
      onBlur={(e) => {
        if (e.target.value !== '') {
          e.target.value = Number(e.target.value).toFixed(2)
          onChange?.(e)
        }

        onBlur?.(e)
      }}
      placeholder={placeholder}
      prefix={prefix}
      ref={ref}
      {...props}
    />
  ),
)

const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  ({ onChange, onValueChange, prefix, postfix, variant, width, disabled, className, ...props }, ref) =>
    prefix || postfix ?
      <Wrapper className={className} variant={variant} width={width} disabled={disabled}>
        <Fixture>{prefix}</Fixture>
        <input
          ref={ref}
          onChange={(e) => {
            onChange?.(e)
            onValueChange?.(e.target.value)
          }}
          disabled={disabled}
          {...props}
        />
        <Fixture>{postfix}</Fixture>
      </Wrapper>
    : <StyledInput
        className={className}
        variant={variant}
        width={width}
        ref={ref}
        onChange={(e) => {
          onChange?.(e)
          onValueChange?.(e.target.value)
        }}
        disabled={disabled}
        {...props}
      />,
)

const Fixture = styled.span`
  color: ${variables.colorBlack60};
`

export const common = css<{ width?: string; variant?: InputVariant; disabled?: boolean }>`
  box-sizing: border-box;
  width: ${(p) => (p.width ? p.width : 'auto')};
  border-color: transparent;
  border-style: solid;
  border-width: 0 0 1px;

  ${(p) =>
    p.disabled &&
    `
    // Matching the ReactSelect default styles (?) here for consistency
    color: #999999;
    background-color: #f2f2f2;
    border-color: #e6e6e6;
  `}

  ${(p) =>
    (p.variant === 'underline' || p.variant === 'outline') &&
    `
      border-color: ${variables.colorBlack20};

      &:hover {
        border-color: ${variables.colorBlack30};
      }      
  `}

  ${(p) =>
    p.variant === 'outline' &&
    `
      padding: 8px;
      border-width: 1px;
      border-radius: 4px;
    `}

  &:focus-within:not([readonly]) {
    border-color: ${variables.colorBlueSecondary};
    outline: none;
  }
`

export const commonInput = css`
  font-family: inherit;
  font-size: 14px;
  color: inherit;
  background-color: transparent;
  border: none;

  ::placeholder {
    color: ${variables.colorBlack50};
  }
`

export const Wrapper = styled.div<{ width?: string; variant?: InputVariant; disabled?: boolean }>`
  ${common}
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  column-gap: 2px;
  align-items: center;

  input {
    ${commonInput}
    &:focus-within {
      border: none;
      outline: none;
    }
  }
`

const StyledInput = styled.input<{ width?: string; variant?: InputVariant; disabled?: boolean }>`
  ${commonInput}
  ${common}
`

export default TextInput
